import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Modal, Field, inputClass, PrimaryButton, EmptyState } from "../components/common";
import { toast } from "sonner";
import { AddressBook, Plus, Trash, PencilSimple, Phone, EnvelopeSimple, User, Buildings } from "@phosphor-icons/react";

const SOURCES = [
  { value: "contrato", label: "Contrato" },
  { value: "orden", label: "Orden de trabajo" },
  { value: "otro", label: "Otro" },
];
const emptyContact = () => ({ name: "", position: "", phone: "", email: "" });
const emptyForm = { company_name: "", rfc: "", source: "contrato", linked_name: "", notes: "", contacts: [emptyContact()] };

export default function CompanyDirectory() {
  const { user } = useAuth();
  const canManage = user?.role !== "contratista";
  const canDelete = ["admin", "gerente_general", "gerente_area"].includes(user?.role) || !!user?.can_delete;
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/company-directory").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm({ ...emptyForm, contacts: [emptyContact()] }); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ company_name: c.company_name || "", rfc: c.rfc || "", source: c.source || "contrato", linked_name: c.linked_name || "", notes: c.notes || "", contacts: (c.contacts && c.contacts.length ? c.contacts.map((x) => ({ ...emptyContact(), ...x })) : [emptyContact()]) }); setOpen(true); };

  const setContact = (i, field, val) => setForm((f) => ({ ...f, contacts: f.contacts.map((c, idx) => idx === i ? { ...c, [field]: val } : c) }));
  const addContact = () => setForm((f) => ({ ...f, contacts: [...f.contacts, emptyContact()] }));
  const removeContact = (i) => setForm((f) => ({ ...f, contacts: f.contacts.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error("Escribe el nombre de la empresa");
    setBusy(true);
    try {
      if (editId) await api.put(`/company-directory/${editId}`, form);
      else await api.post("/company-directory", form);
      toast.success(editId ? "Empresa actualizada" : "Empresa agregada"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("¿Eliminar esta empresa del directorio?")) return; try { await api.delete(`/company-directory/${id}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const shown = items.filter((c) => !q || c.company_name.toLowerCase().includes(q.toLowerCase()) || (c.rfc || "").toLowerCase().includes(q.toLowerCase()) || (c.contacts || []).some((p) => (p.name || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div>
      <PageHeader overline="Contactos de obra" title="Directorio de Empresas"
        action={canManage && <PrimaryButton onClick={openNew} data-testid="add-company-btn"><Plus size={18} weight="bold" /> Nueva empresa</PrimaryButton>} />

      <div className="mb-5">
        <input data-testid="dir-search" className={inputClass + " max-w-sm"} placeholder="Buscar empresa, RFC o contacto…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={AddressBook} title="Sin empresas" subtitle="Registra las empresas contratadas (por contrato u orden de trabajo) y sus contactos."
          action={canManage && <PrimaryButton onClick={openNew}><Plus size={18} weight="bold" /> Nueva empresa</PrimaryButton>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shown.map((c) => (
            <Card key={c.id} data-testid={`company-${c.id}`} className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-lg flex items-center gap-2"><Buildings size={18} weight="fill" className="text-primary" /> {c.company_name}</div>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                    <span className="px-2 py-0.5 rounded-sm font-semibold bg-indigo-50 text-indigo-700">{(SOURCES.find((s) => s.value === c.source) || {}).label || c.source}</span>
                    {c.linked_name && <span className="text-muted-foreground">{c.linked_name}</span>}
                    {c.rfc && <span className="text-muted-foreground">RFC: {c.rfc}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {canManage && <button onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`} className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"><PencilSimple size={14} weight="bold" /> Editar</button>}
                  {canDelete && <button onClick={() => remove(c.id)} data-testid={`delete-company-${c.id}`} className="text-muted-foreground hover:text-danger"><Trash size={16} /></button>}
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {(c.contacts || []).length === 0 && <div className="text-sm text-muted-foreground">Sin contactos.</div>}
                {(c.contacts || []).map((p, i) => (
                  <div key={i} className="border border-border rounded-sm p-2.5 text-sm">
                    <div className="font-semibold flex items-center gap-1.5"><User size={14} weight="fill" className="text-muted-foreground" /> {p.name || "—"}{p.position && <span className="font-normal text-muted-foreground">· {p.position}</span>}</div>
                    <div className="flex flex-wrap gap-4 mt-1 text-xs">
                      {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline"><Phone size={13} weight="fill" /> {p.phone}</a>}
                      {p.email && <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline"><EnvelopeSimple size={13} weight="fill" /> {p.email}</a>}
                    </div>
                  </div>
                ))}
              </div>
              {c.notes && <p className="text-xs text-muted-foreground mt-3">{c.notes}</p>}
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Editar empresa" : "Nueva empresa"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Nombre de la empresa *"><input required data-testid="company-name" className={inputClass} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Origen">
              <select data-testid="company-source" className={inputClass} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="RFC (opcional)"><input data-testid="company-rfc" className={inputClass} value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value })} /></Field>
          </div>
          <Field label="Contrato / Orden relacionada (opcional)"><input data-testid="company-linked" className={inputClass} placeholder="Ej. Contrato ALSA, OT Limpieza…" value={form.linked_name} onChange={(e) => setForm({ ...form, linked_name: e.target.value })} /></Field>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold">Contactos (responsables)</label>
              <button type="button" onClick={addContact} data-testid="add-contact-btn" className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"><Plus size={13} weight="bold" /> Agregar contacto</button>
            </div>
            <div className="space-y-3">
              {form.contacts.map((c, i) => (
                <div key={i} data-testid={`contact-row-${i}`} className="border border-border rounded-sm p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input className={inputClass} placeholder="Nombre" data-testid={`contact-name-${i}`} value={c.name} onChange={(e) => setContact(i, "name", e.target.value)} />
                    <input className={inputClass} placeholder="Puesto en la empresa" data-testid={`contact-position-${i}`} value={c.position} onChange={(e) => setContact(i, "position", e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input className={inputClass} placeholder="Teléfono" data-testid={`contact-phone-${i}`} value={c.phone} onChange={(e) => setContact(i, "phone", e.target.value)} />
                    <input className={inputClass} placeholder="Correo" type="email" data-testid={`contact-email-${i}`} value={c.email} onChange={(e) => setContact(i, "email", e.target.value)} />
                  </div>
                  {form.contacts.length > 1 && <button type="button" onClick={() => removeContact(i)} className="text-xs text-danger hover:underline inline-flex items-center gap-1"><Trash size={13} /> Quitar contacto</button>}
                </div>
              ))}
            </div>
          </div>

          <Field label="Notas (opcional)"><textarea className={inputClass + " h-20 py-2"} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-company-btn">{busy ? "Guardando…" : (editId ? "Guardar cambios" : "Agregar empresa")}</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
